/**
 * @author Akshay Kumar Singh
 * @file UserManagement Component
 * @flow
 */
import React, { useState, useEffect } from 'react';
import CreateUserModal from '../../shared/modals/CreateUserModal';
import FAQUploadModal from '../../shared/modals/FAQUploadModal';
import BulkUploadModal from '../../shared/modals/BulkUploadModal';
import AlertModal from '../../shared/modals/AlertModal';
import { spinnerService } from '../../shared/services/spinner.service';
import { CallApi } from '../../shared/services/api.service'
import CONFIG from '../../configuration/config';
import { useHistory } from "react-router-dom";
import SubHeaderComponent from '../common/HeaderComponent/SubHeaderComponent';
import Pagination from '../common/Pagination';
import { sortArrObj } from '../../shared/utils/helper.util';
import { setData, getData } from '../../shared/utils/storage.util';
import { LABLES, STRINGS, USER_ROLES } from '../../shared/constants/data';

const UserManagement = () => {
  const history = useHistory();
  const [state, setState] = useState({
    roleArr: [
      'audit manager',
      'auditor',
      'reviewer',
      'plant manager',
      'plant auditor',
      'plant reviewer',
    ],
    dataArr: [],
  });

  const [searchedItem, setSearchedItem] = useState('')
  const [filteredData, setFilteredData] = useState([])
  const [isModalOpen, setModalOpen] = useState(false);
  const [isFAQModalOpen, toggleFAQModal] = useState(false);
  const [isBulkUploadModalOpen, toggleBulkModal] = useState(false);
  const [statusCheck, setStatus] = useState(true);
  const [plantArr, setPlantArr] = useState([]);
  const [tempDataArr, setTempDataArr] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [endPage, setEndPage] = useState(1);

  /**
   * @description This function is react life cycle hook which sets initial values whenever the component loads.
   */
  useEffect(() => {
    getUserDataList();
    getPlantDataList();
  }, [])

  /**
   * @description This function is react life cycle hook which sets filteredData value
   * and set current page as 1st page on change in searched value.
   */
  useEffect(() => {
    if (checkEditable()) {
      let data = state.dataArr.filter(item => {
        return ((item.first_name.toLowerCase().indexOf(
          searchedItem.toLowerCase()) !== -1) || (item.last_name.toLowerCase().indexOf(
            searchedItem.toLowerCase()) !== -1) || (item.email.toLowerCase().indexOf(
              searchedItem.toLowerCase()) !== -1))
      })
      setFilteredData(data);
      setData('userFilteredList', data)
      setCurrentPage(1);
    } else {
      editAlert();
    }
  }, [searchedItem]);

  /**
   * @description This function opens the create user modal.
   */
  const openModal = event => {
    event.preventDefault();
    setModalOpen(true);
  }

  /**
   * @description This function closes the create user modal.
   */
  const closeModal = () => {
    setModalOpen(false);
  }

  /**
   * @description This function toggles the FAQ modal.
   */
  const toggleFAQUploadModal = () => {
    toggleFAQModal(!isFAQModalOpen);
  }

  /**
   * @description This function toggles the FAQ modal.
   */
  const toggleBulkUploadModal = () => {
    toggleBulkModal(!isBulkUploadModalOpen);
  }

  /**
   * @description This function fetch all plants data.
   */
  const getPlantDataList = async () => {
    spinnerService.show(LABLES.Oval, STRINGS.FetchPlant);
    await CallApi(LABLES.POST, CONFIG.plantList, {}, {}, '', getPlantList, history);
  }

  /**
   * @description Plant List API callback function
   * @param response This is the response structure received from API
   */
  const getPlantList = (response) => {
    if (response?.success) {
      setPlantArr(response.response.plant);
    }
  }

  /**
   * @description This function get all users data.
   */
  const getUserDataList = async () => {
    spinnerService.show(LABLES.Oval, STRINGS.FetchUserData);
    await CallApi(LABLES.POST, CONFIG.getUserList, {}, {}, '', getUserList, history);
  }

  /**
   * @description getUserList API callback function
   * @param response This is the response structure received from API
   */
  const getUserList = (response) => {
    if (response?.success) {
      setState({
        ...state,
        dataArr: response.response
      })
      let data = response.response.map(item => ({ ...item }))
      setFilteredData(response.response);
      setData('userList', sortArrObj(response.response, 'first_name'))
      setTempDataArr(sortArrObj(data, 'first_name'));
      setEndPage(Math.ceil(response.response.length / dataPerPage));
    }
  }

  /**
   * @description This function updates user data in dataArr.
   */
  const handleChange = (event, id) => {
    const { dataArr } = state;
    const { name, value } = event.target;
    // dataArr[id][name] = value
    if (filteredData.length) {
      filteredData[id][name] = value
    }
    setState({
      ...state,
      dataArr,
      filteredData
    })
    // setFilteredData(filteredData)
    if (filteredData.length && (filteredData[id]['role_name'] === USER_ROLES.AuditManager || filteredData[id]['role_name'] === USER_ROLES.Auditor || filteredData[id]['role_name'] === USER_ROLES.Reviewer)) {
      filteredData[id]['plant_id'] = '';
      setState({
        ...state,
        dataArr,
        filteredData
      })
    }
  }

  /**
   * @description This function user status to be active or in-active.
   */
  const handleStatusChange = (event, id) => {
    const { dataArr } = state;
    const { name } = event.target;
    if (id || id === 0) {
      setStatus(!statusCheck)
      if (!statusCheck) {
        if (tempDataArr[id][name] === 0 || tempDataArr[id][name] === 2) {
          // dataArr[id][name] = 0;
          filteredData[id][name] = 0;
        } else if (tempDataArr[id][name] === 1) {
          // dataArr[id][name] = 1;
          filteredData[id][name] = 1;
        }
      } else {
        // dataArr[id][name] = 2
        filteredData[id][name] = 2;
      }
      setState({
        ...state,
        dataArr,
        filteredData
      })
      // setFilteredData(filteredData);
    }
  }

  /**
   * @description This function update user data.
   */
  const handleUpdate = async (event, id) => {
    // const { dataArr } = state;
    let check = false;
    if (filteredData[id]['role_name'] === USER_ROLES.AuditManager || filteredData[id]['role_name'] === USER_ROLES.Auditor || filteredData[id]['role_name'] === USER_ROLES.Reviewer) {
      check = true;
    } else {
      if (filteredData[id]['plant_id']) {
        check = true;
      } else {
        check = false;
      }
    }

    if (!!filteredData[id]['first_name'] && check) {
      filteredData[id]['isEditable'] = false
      setState({
        ...state,
        filteredData
      })
      // setFilteredData(filteredData)
      spinnerService.show(LABLES.Oval, STRINGS.UpdateUserDetails);
      await CallApi(LABLES.POST, CONFIG.updateUser, {}, filteredData[id], '', updateUser, history);
    } else {
      AlertModal.showAlert(STRINGS.AllUserFields)
    }
  }

  /**
   * @description updateUser callback function.
   * @param response This is the response structure received from API.
   */
  const updateUser = (response) => {
    if (response?.success) {
      AlertModal.showAlert(response?.message);
      setTimeout(() => {
        AlertModal.hideAlert();
        getUserDataList();
      }, 2000);
    }
  }

  /**
   * @description This function turns the field editable.
   */
  const editData = (event, id) => {
    event.preventDefault();
    // const { dataArr } = state;
    let check = true;
    filteredData.forEach((data) => {
      if (data?.isEditable === true) {
        check = false;
      }
    })
    if (check) {
      filteredData[id]['isEditable'] = true
      setState({
        ...state,
        // dataArr,
        filteredData
      })
      // setFilteredData(filteredData)
    } else {
      AlertModal.showAlert(STRINGS.OperationAlertMsg)
    }
  }

  /**
   * @description This function restore data to its previous state
   */
  const restoreData = (event, id) => {
    event.preventDefault();
    let userDataArr = getData('userList')
    let filteredDataArr = getData('userFilteredList')
    const { dataArr } = state;
    if (id || id == 0) {
      dataArr[id] = userDataArr[id]
      filteredData[id] = filteredDataArr.length ? filteredDataArr[id] : userDataArr[id]
      setState({
        ...state,
        dataArr,
        filteredData
      })
      // setFilteredData(filteredData)
    }
  }

  /**
   * @description This function check if the function is editable or not.
   */
  const checkEditable = () => {
    const { dataArr } = state;
    let check = true;
    filteredData.map((data, index) => {
      if (data?.isEditable === true) {
        check = false;
      }
    })
    return check;
  }

  let indexOfLastData = currentPage * dataPerPage;
  let indexOfFirstData = indexOfLastData - dataPerPage;

  /**
   * @description This function updates the directly updates the page number.
   * @param pageNumber It has value of page number.
   */
  const paginate = (pageNumber) => {
    // event.preventDefault();
    if (checkEditable()) {
      setCurrentPage(pageNumber);
    } else {
      editAlert();
    }
  }

  /**
   * @description This function increment page number.
   */
  const pageIncrement = (event) => {
    event.preventDefault();
    if (checkEditable()) {
      if (currentPage < endPage) {
        setCurrentPage(currentPage + 1);
      }
    } else {
      editAlert();
    }
  }

  /**
   * @description This function decrement page number.
   */
  const pageDecrement = (event) => {
    event.preventDefault();
    if (checkEditable()) {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } else {
      editAlert();
    }
  }

  /**
   * @description This function set first page as current page.
   */
  const paginateFirstPage = (event) => {
    event.preventDefault();
    if (checkEditable()) {
      setCurrentPage(1);
    } else {
      editAlert();
    }
  }

  /**
   * @description This function set last page as current page.
   */
  const paginateLastPage = (event) => {
    event.preventDefault();
    if (checkEditable()) {
      setCurrentPage(endPage);
      indexOfLastData = endPage * dataPerPage;
      indexOfFirstData = indexOfLastData - dataPerPage;
    } else {
      editAlert();
    }
  }

  /**
   * @description This function contains alert message to be shown.
   */
  const editAlert = () => {
    AlertModal.showAlert(STRINGS.OperationAlertMsg);
  }

  return (
    <React.Fragment>
      <SubHeaderComponent page={1} pageName={LABLES.UserManagement} dataArr={state.dataArr} setSearchedItem={setSearchedItem} />
      <div className="super-admin">
        <label>{STRINGS.ListUsers}</label>
        {/* <input type='text' name='searchedItem' onChange={(event) => setSearchedItem(event.target.value)} disabled={!state.dataArr.length} /> */}
        <div className='user-menu-btn'>
          <button type='button' className="btn-prmry" onClick={toggleBulkUploadModal}>{LABLES.BulkUpload}</button>
          <button type='button' className="btn-prmry" onClick={toggleFAQUploadModal}>{LABLES.UploadFAQ}</button>
          <button type='button' className="btn-prmry" onClick={openModal}>{LABLES.CreateUser}</button>
        </div>
      </div>
      <div className="main-cntnt-wrap">
        <div className="container">
          <div className="plant-summary-wrapper">
            <div className="tasks-tabs">
              <div className="table-responsive">
                <table className="add-task-tble user-table large-table">
                  <tbody>
                    <tr>
                      <th style={{ width: '15%', textAlign: 'center' }}>{LABLES.FirstName}</th>
                      <th style={{ width: '15%', textAlign: 'center' }}>{LABLES.LastName}</th>
                      <th style={{ width: '20%', textAlign: 'center' }}>{LABLES.Email}</th>
                      <th style={{ width: '12%', textAlign: 'center' }}>{LABLES.Role}</th>
                      <th style={{ width: '13%', textAlign: 'center' }}>{LABLES.PlantName}</th>
                      <th style={{ width: '15%', textAlign: 'center' }}>{LABLES.Status}</th>
                      <th style={{ width: '10%', textAlign: 'center' }}>{LABLES.Action}</th>
                    </tr>
                    {filteredData.map((data, index) => {
                      return (
                        (index >= indexOfFirstData && index < indexOfLastData) &&
                        <tr key={index} className={!!data?.isEditable ? "row-editable" : ""}>
                          <td><input type="text" className="form-control" name='first_name' onChange={(e) => handleChange(e, index)} value={data?.first_name} readOnly={!data?.isEditable} /></td>
                          <td><input type="text" className="form-control" name='last_name' onChange={(e) => handleChange(e, index)} value={data?.last_name} readOnly={!data?.isEditable} /></td>
                          <td>{data?.email}</td>
                          <td>
                            <select className="form-control capital-case" name='role_name' onChange={(e) => handleChange(e, index)} value={data.role_name.toLowerCase() ? data.role_name.toLowerCase() : ''} disabled={!data?.isEditable} >
                              <option value='' hidden>{STRINGS.SelectRole}</option>
                              {state.roleArr.map((element, index) => {
                                return (
                                  <option key={element} value={element} >
                                    {element}
                                  </option>
                                )
                              })}
                            </select>
                          </td>
                          <td>
                            {(data.role_name === 'plant manager' || data.role_name === 'plant auditor' || data.role_name === 'plant reviewer') &&
                              <select className="form-control" name='plant_id' onChange={(e) => handleChange(e, index)} value={data.plant_id ? data.plant_id : ''} disabled={!data?.isEditable} >
                                <option value='' hidden>{STRINGS.SelectPlant}</option>
                                {plantArr.map((item, index) => {
                                  return (
                                    <option key={item.plant_id} value={item.plant_id} >
                                      {item.plant_name}
                                    </option>
                                  )
                                })}
                              </select>
                            }
                          </td>
                          <td className="td-center">
                            <button type='button' name='is_active' onClick={(e) => handleStatusChange(e, index)} className={!data?.isEditable ? "btn-scndry disabled" : "btn-prmry"} >{data?.is_active === 0 || data?.is_active === 1 ? LABLES.Active : LABLES.InActive}</button>
                          </td>
                          <td>
                            <a href="" onClick={(e) => editData(e, index)} hidden={!!data?.isEditable}><img src={require("../../assets/images/edit-icon.png")} alt="edit-icon" /></a>
                            <button type='button' name='update' onClick={(e) => handleUpdate(e, index)} className={"btn-update"} hidden={!data?.isEditable}><img src={require("../../assets/images/right-check.png")} alt="cancel-icon" /></button>
                            <a href="" onClick={(e) => restoreData(e, index)} hidden={!data?.isEditable}><img src={require("../../assets/images/cancel-icon.png")} alt="cancel-icon" /></a>
                          </td>
                        </tr>
                      )
                    })
                    }
                  </tbody>
                </table>
              </div>
              <Pagination dataPerPage={dataPerPage} currentPage={currentPage} totalData={filteredData.length} paginate={paginate}
                pageIncrement={pageIncrement} firstPage={1} lastPage={endPage} pageDecrement={pageDecrement} paginateFirstPage={paginateFirstPage} paginateLastPage={paginateLastPage} />
            </div>
          </div>
        </div>
      </div>
      {(isModalOpen && plantArr.length) &&
        <CreateUserModal isModalOpen={isModalOpen} closeModal={closeModal} getUserDataList={getUserDataList} plantArr={plantArr} />
      }
      {
        isFAQModalOpen &&
        <FAQUploadModal isFAQModalOpen={isFAQModalOpen} closeModal={toggleFAQUploadModal} />
      }
      {
        isBulkUploadModalOpen &&
        <BulkUploadModal isBulkUploadModalOpen={isBulkUploadModalOpen} getUserDataList={getUserDataList} closeModal={toggleBulkUploadModal} />
      }
    </React.Fragment>
  );
}

export default UserManagement;